/**
 * Theme-specific formatting utilities
 *
 * GraphQL transformation utilities for mapping Magento data to ProductCard.
 */

import type { Product } from '../../UiBlocks'

/** GraphQL product type for mapGraphQLProductToProduct */
export interface GraphQLProduct {
  id?: number | string
  sku?: string
  name?: string
  type_id?: string
  url_rewrites?: { url: string }[]
  price_range?: {
    minimum_price?: {
      regular_price?: { value?: number }
      final_price?: { value?: number }
    }
  }
  customProductImageUrl?: { url?: string }
  custom_attributes?: { attribute_code: string; value: string }[]
  new_from_date?: string | null
  new_to_date?: string | null
  media_gallery?: {
    url: string
    label: string
    video_content?: { video_url: string } | null
  }[]
}

/** Valid Magento product types */
const VALID_PRODUCT_TYPES = ['simple', 'configurable', 'grouped', 'bundle', 'virtual', 'downloadable'] as const
type ValidProductType = (typeof VALID_PRODUCT_TYPES)[number]

/** Type guard for valid product types */
function isValidProductType(value: unknown): value is ValidProductType {
  return typeof value === 'string' && VALID_PRODUCT_TYPES.includes(value as ValidProductType)
}

/**
 * Find custom attribute value from product custom_attributes array
 */
function findCustomAttributeValue(
  customAttributes: GraphQLProduct['custom_attributes'],
  attributeCode: string
): string | null {
  if (!customAttributes) return null
  const attribute = customAttributes.find(
    (attr) => attr.attribute_code === attributeCode
  )
  return attribute ? attribute.value : null
}

/**
 * Map GraphQL product to ProductCard Product interface
 *
 * Transforms raw GraphQL product data (from Magento queries) to the
 * simplified Product interface used by ProductCard component.
 *
 * @param product - Raw GraphQL product object
 * @returns Product interface for ProductCard
 */
/** Check if a date range marks the product as "new" */
function isProductNew(newFromDate?: string | null, newToDate?: string | null): boolean {
  if (!newFromDate) return false
  const now = new Date()
  const from = new Date(newFromDate)
  if (now < from) return false
  if (newToDate) {
    const to = new Date(newToDate)
    if (now > to) return false
  }
  return true
}

/** Check if media gallery contains video entries */
function hasProductVideo(mediaGallery?: GraphQLProduct['media_gallery']): boolean {
  if (!mediaGallery) return false
  return mediaGallery.some((item) => item.video_content?.video_url)
}

export function mapGraphQLProductToProduct(product: GraphQLProduct): Product {
  const baseUrl = typeof window !== 'undefined' ? (window as { BASE_URL?: string }).BASE_URL || '' : ''
  const urlRewrite = product.url_rewrites?.[0]?.url || ''

  const regularPrice = product.price_range?.minimum_price?.regular_price?.value ?? 0
  const finalPrice = product.price_range?.minimum_price?.final_price?.value ?? regularPrice
  const msrpValue = parseFloat(
    findCustomAttributeValue(product.custom_attributes, 'msrp') || '0'
  )

  // Only set specialPrice if it's different from regular price (actual discount)
  const specialPrice = finalPrice < regularPrice ? finalPrice : null

  return {
    id: product.id ?? product.sku ?? '',
    sku: product.sku ?? '',
    name: product.name ?? '',
    url: `${baseUrl}${urlRewrite}`,
    imageUrl: product.customProductImageUrl?.url ?? '',
    price: regularPrice,
    specialPrice,
    msrp: msrpValue > 0 ? msrpValue : null,
    productType: isValidProductType(product.type_id) ? product.type_id : 'simple',
    isNew: isProductNew(product.new_from_date, product.new_to_date),
    hasVideo: hasProductVideo(product.media_gallery),
  }
}
